<!-- MyEvents.vue -->
<template>
  <div id="layout-wrapper">
    <NavigationBar/>

    <!-- ============================================================== -->
    <!-- Start right Content here -->
    <!-- ============================================================== -->
    <div id="content" class="content-minimize">

      <div class="page-content">

        <div class="container-fluid">

          <div>
            <!-- Page header -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">My Events</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="index.html">Home</a></li>
                      <li class="breadcrumb-item active">HR Management</li>
                      <li class="breadcrumb-item active">My Events</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tabs for filtering events -->
            <div class="card">
              <div class="card-body">
                <ul class="nav nav-tabs nav-tabs-custom" role="tablist">
                  <li class="nav-item">
                    <a class="nav-link" :class="{ active: activeTab === 'upcoming' }" @click="activeTab = 'upcoming'" href="javascript: void(0);" role="tab">
                      <i class="mdi mdi-calendar-clock mr-1"></i> Upcoming Events
                    </a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link" :class="{ active: activeTab === 'past' }" @click="activeTab = 'past'" href="javascript: void(0);" role="tab">
                      <i class="mdi mdi-calendar-check mr-1"></i> Past Events
                    </a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link" :class="{ active: activeTab === 'pending' }" @click="activeTab = 'pending'" href="javascript: void(0);" role="tab">
                      <i class="mdi mdi-calendar-alert mr-1"></i> Pending Response
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Loading state -->
            <div v-if="loading" class="text-center p-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading your events...</p>
            </div>

            <!-- Empty state -->
            <div v-else-if="filteredEvents.length === 0" class="card">
              <div class="card-body text-center p-5">
                <i class="mdi mdi-calendar-remove font-size-48 text-muted"></i>
                <h5 class="mt-2">No events found</h5>
                <p class="text-muted" v-if="activeTab === 'upcoming'">You don't have any upcoming events.</p>
                <p class="text-muted" v-else-if="activeTab === 'past'">You don't have any past events.</p>
                <p class="text-muted" v-else>You don't have any events requiring your response.</p>
              </div>
            </div>

            <!-- Events list -->
            <div v-else class="row">
              <div class="col-12">
                <div v-for="event in filteredEvents" :key="event.id" class="card mb-3">
                  <div class="card-body">
                    <div class="d-flex align-items-start">
                      <!-- Event date badge -->
                      <div class="event-date-badge text-center me-4">
                        <span class="date-month">{{ formatDateMonth(event.start) }}</span>
                        <span class="date-day fs-1 fw-bold d-block">{{ formatDateDay(event.start) }}</span>
                        <span class="badge rounded-pill" :class="getEventTypeColor(event.extendedProps.eventType)">
                  {{ event.extendedProps.eventType }}
                </span>
                      </div>

                      <!-- Event details -->
                      <div class="flex-grow-1">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <h5 class="card-title mb-0">{{ event.title }}</h5>
                          <span class="badge" :class="getStatusBadgeClass(event.extendedProps.status)">
                    {{ event.extendedProps.status }}
                  </span>
                        </div>

                        <div class="event-details mb-3">
                          <p class="mb-1"><i class="mdi mdi-calendar me-1"></i> {{ formatDate(event.start) }} {{ event.end ? '- ' + formatDate(event.end) : '' }}</p>
                          <p class="mb-1"><i class="mdi mdi-clock-outline me-1"></i> {{ formatEventTime(event) }}</p>
                          <p class="mb-1"><i class="mdi mdi-map-marker me-1"></i> {{ event.extendedProps.location || 'No location specified' }}</p>
                          <p v-if="event.extendedProps.department" class="mb-1">
                            <i class="mdi mdi-office-building me-1"></i> {{ event.extendedProps.department }}
                          </p>
                          <p v-if="event.extendedProps.description" class="mt-2">
                            {{ event.extendedProps.description }}
                          </p>
                        </div>

                        <!-- Response controls -->
                        <div v-if="canRespond(event)" class="event-response mt-3">
                          <div v-if="respondingTo === event.id">
                            <div class="form-group mb-3">
                              <label class="form-label">Your Response</label>
                              <textarea v-model="responseComment" class="form-control" rows="2" placeholder="Add a comment (optional)"></textarea>
                            </div>
                            <div class="d-flex">
                              <button @click="respondToEvent(event.id, 'Accepted')"
                                      class="btn btn-success me-2"
                                      :disabled="isSubmitting">
                        <span v-if="isSubmitting && submittingEventId === event.id && submittingResponse === 'Accepted'">
                          <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Accepting...
                        </span>
                                <span v-else>
                          <i class="mdi mdi-check me-1"></i> Accept
                        </span>
                              </button>
                              <button @click="respondToEvent(event.id, 'Tentative')"
                                      class="btn btn-warning me-2"
                                      :disabled="isSubmitting">
                        <span v-if="isSubmitting && submittingEventId === event.id && submittingResponse === 'Tentative'">
                          <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Submitting...
                        </span>
                                <span v-else>
                          <i class="mdi mdi-help-circle me-1"></i> Maybe
                        </span>
                              </button>
                              <button @click="respondToEvent(event.id, 'Declined')"
                                      class="btn btn-danger me-2"
                                      :disabled="isSubmitting">
                        <span v-if="isSubmitting && submittingEventId === event.id && submittingResponse === 'Declined'">
                          <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Declining...
                        </span>
                                <span v-else>
                          <i class="mdi mdi-close me-1"></i> Decline
                        </span>
                              </button>
                              <button @click="cancelResponse()"
                                      class="btn btn-light"
                                      :disabled="isSubmitting">
                                Cancel
                              </button>
                            </div>
                          </div>
                          <div v-else>
                            <button @click="startResponse(event.id)" class="btn btn-primary">
                              <i class="mdi mdi-reply me-1"></i> Respond to Event
                            </button>
                          </div>
                        </div>

                        <!-- Current response -->
                        <div v-else-if="event.extendedProps.myResponse" class="event-current-response mt-3">
                          <div class="d-flex align-items-center">
                            <span class="me-2">Your response:</span>
                            <span class="badge rounded-pill" :class="getResponseBadgeClass(event.extendedProps.myResponse)">
                      {{ event.extendedProps.myResponse }}
                    </span>
                            <button v-if="!isPastEvent(event)"
                                    @click="startResponse(event.id)"
                                    class="btn btn-sm btn-outline-primary ms-2"
                                    :disabled="isSubmitting">
                              Change
                            </button>
                          </div>
                          <p v-if="event.extendedProps.myComment" class="text-muted mt-2">
                            "{{ event.extendedProps.myComment }}"
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Response confirmation modal -->
            <div class="modal fade" id="responseConfirmationModal" tabindex="-1" aria-hidden="true">
              <div class="modal-dialog">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">Response Submitted</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                  </div>
                  <div class="modal-body text-center">
                    <div v-if="lastResponse === 'Accepted'" class="mb-3">
                      <i class="mdi mdi-check-circle text-success" style="font-size: 48px;"></i>
                      <h4 class="mt-2">Event Accepted</h4>
                    </div>
                    <div v-else-if="lastResponse === 'Declined'" class="mb-3">
                      <i class="mdi mdi-close-circle text-danger" style="font-size: 48px;"></i>
                      <h4 class="mt-2">Event Declined</h4>
                    </div>
                    <div v-else-if="lastResponse === 'Tentative'" class="mb-3">
                      <i class="mdi mdi-help-circle text-warning" style="font-size: 48px;"></i>
                      <h4 class="mt-2">Tentative Response Sent</h4>
                    </div>
                    <p>Your response to the event has been submitted successfully.</p>
                    <p v-if="lastEventTitle" class="text-muted">{{ lastEventTitle }}</p>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Close</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute, toast } from "@/api";
import NavigationBar from "@/components/Navigation.vue";

export default {
  name: "MyEvents",
  components: {NavigationBar},
  data() {
    return {
      loading: true,
      myEvents: [],
      activeTab: 'upcoming',
      responseComment: '',
      respondingTo: null,
      isSubmitting: false,
      submittingEventId: null,
      submittingResponse: null,
      lastResponse: null,
      lastEventTitle: ''
    };
  },
  computed: {
    filteredEvents() {
      const now = new Date();

      if (this.activeTab === 'upcoming') {
        return this.myEvents.filter(event => {
          const eventStart = new Date(event.start);
          return eventStart >= now && event.extendedProps.status !== 'Canceled';
        });
      } else if (this.activeTab === 'past') {
        return this.myEvents.filter(event => {
          const eventStart = new Date(event.start);
          return eventStart < now || event.extendedProps.status === 'Canceled';
        });
      } else if (this.activeTab === 'pending') {
        return this.myEvents.filter(event => {
          const eventStart = new Date(event.start);
          return eventStart >= now &&
              event.extendedProps.status !== 'Canceled' &&
              (!event.extendedProps.myResponse || event.extendedProps.myResponse === 'Pending');
        });
      }

      return this.myEvents;
    }
  },
  mounted() {
    this.fetchMyEvents();
  },
  methods: {
    async fetchMyEvents() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getCalendarEvents");

        const response = await execute(data, authUrl);
        if (response.data.success) {
          this.myEvents = response.data.data.events || [];

          // Process events to include response status
          this.myEvents.forEach(event => {
            // Check if current user is in attendees and get their response
            if (event.extendedProps.attendeesList) {
              const currentUser = event.extendedProps.attendeesList.find(
                  a => a.employeeId === this.getCurrentUserId()
              );

              if (currentUser) {
                event.extendedProps.myResponse = currentUser.response || 'Pending';
                event.extendedProps.myComment = currentUser.comments || '';
              }
            }
          });
        } else {
          console.error("Error fetching events:", response.data.message);
          toast.error("Failed to load your events");
        }
      } catch (error) {
        console.error("API error:", error);
        toast.error("Network error when fetching events");
      } finally {
        this.loading = false;
      }
    },

    getCurrentUserId() {
      // This should return the current user's ID from your auth system
      // Replace with actual implementation
      return "1"; // Example user ID
    },

    async respondToEvent(eventId, responseType) {
      this.isSubmitting = true;
      this.submittingEventId = eventId;
      this.submittingResponse = responseType;

      try {
        const data = new FormData();
        data.append("function", "respondToEvent");
        data.append("eventId", eventId);
        data.append("response", responseType);
        data.append("comments", this.responseComment);

        const response = await execute(data, authUrl);

        if (response && response.data && response.data.success) {
          // Success handling
          toast.success(`You have ${responseType.toLowerCase()} the event`);
          this.lastResponse = responseType;

          // Find the event and update its response status
          const event = this.myEvents.find(e => e.id === eventId);
          if (event) {
            event.extendedProps.myResponse = responseType;
            event.extendedProps.myComment = this.responseComment;
            this.lastEventTitle = event.title;
          }

          // Reset response UI
          this.respondingTo = null;
          this.responseComment = '';

          // Show confirmation modal
          const modal = new bootstrap.Modal(document.getElementById('responseConfirmationModal'));
          modal.show();
        } else {
          // Error handling when response exists but success is false
          const errorMessage = (response && response.data && response.data.message)
              ? response.data.message
              : "Failed to submit your response";
          toast.error(errorMessage);
        }
      } catch (error) {
        // Safe error handling
        console.error("API error:", error);
        toast.error("Network error when responding to event");
      } finally {
        this.isSubmitting = false;
        this.submittingEventId = null;
        this.submittingResponse = null;
      }
    },

    startResponse(eventId) {
      this.respondingTo = eventId;
      this.responseComment = '';
    },

    cancelResponse() {
      this.respondingTo = null;
      this.responseComment = '';
    },

    canRespond(event) {
      // Can respond if:
      // 1. Event is in the future
      // 2. Event is not canceled
      // 3. Either there's no previous response or we're currently responding to this event
      const eventStart = new Date(event.start);
      const now = new Date();

      return eventStart >= now &&
          event.extendedProps.status !== 'Canceled' &&
          (this.respondingTo === event.id || !event.extendedProps.myResponse || event.extendedProps.myResponse === 'Pending');
    },

    isPastEvent(event) {
      const eventStart = new Date(event.start);
      const now = new Date();
      return eventStart < now;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateMonth(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short' });
    },

    formatDateDay(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.getDate();
    },

    formatEventTime(event) {
      if (event.allDay) return 'All Day';

      const startDate = new Date(event.start);
      let timeString = startDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      if (event.end) {
        const endDate = new Date(event.end);
        timeString += ' - ' + endDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      }

      return timeString;
    },

    getEventTypeColor(type) {
      if (!type) return 'bg-secondary';

      const typeMap = {
        'Meeting': 'bg-success',
        'Training': 'bg-purple',
        'Holiday': 'bg-warning',
        'Deadline': 'bg-danger',
        'Conference': 'bg-info',
        'Team Building': 'bg-success',
        'Announcement': 'bg-warning',
        'Other': 'bg-secondary'
      };

      return typeMap[type] || 'bg-secondary';
    },

    getStatusBadgeClass(status) {
      if (!status) return 'bg-secondary';

      const statusMap = {
        'Scheduled': 'bg-success',
        'Canceled': 'bg-danger',
        'Completed': 'bg-info',
        'In Progress': 'bg-warning',
        'Postponed': 'bg-warning'
      };

      return statusMap[status] || 'bg-secondary';
    },

    getResponseBadgeClass(response) {
      if (!response) return 'bg-secondary';

      const responseMap = {
        'Accepted': 'bg-success',
        'Declined': 'bg-danger',
        'Tentative': 'bg-warning',
        'Pending': 'bg-secondary'
      };

      return responseMap[response] || 'bg-secondary';
    }
  }
};
</script>

<style scoped>
.event-date-badge {
  min-width: 70px;
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 10px;
}

.date-month {
  text-transform: uppercase;
  font-size: 14px;
  color: #6c757d;
}

.date-day {
  color: #495057;
}

.nav-tabs-custom .nav-link {
  border-bottom: 2px solid transparent;
}

.nav-tabs-custom .nav-link.active {
  color: #556ee6;
  border-bottom: 2px solid #556ee6;
}
</style>